import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ChevronRight, Check, ArrowLeft } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import useTenantState from '@/states/tenant.state';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import { useRefetchCompletionStatus } from '@/hooks/useCompletionStatus';
import useOrgChartState from '@/states/organization.state';
import useKpiState from '@/states/kpi.state';
import {
  kpiFormSchema,
  KpiFormValues,
  KpiItemValues,
  defaultKpiItem,
  KPI_SATUAN_OPTIONS,
  KPI_ARAH_OPTIONS,
  KPI_MAX_TOTAL_BOBOT,
  sumKpiBobot,
  getDefaultTargetKpi,
  getDefaultTargetValue,
  calcPencapaian,
  formatThousandSeparator,
  stripThousandSeparator,
  formatPuebi,
  isKpiEligibleFrequency,
} from './kpi/kpi.schema';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { ORG_DIVISIONS, ORG_LEVELS } from '@/utils/constants/organization-matrix';
import StepTipsPanel from '@/components/company/StepTipsPanel';

// ─── Debounce ─────────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// ─── Padding helper (avoids nested ternary in JSX) ───────────────────────────

function getPaddingClass(satuan: string): string {
  if (satuan === 'rupiah') return 'pl-7 pr-2';
  if (satuan === 'persen') return 'pr-6 pl-2';
  return 'px-2';
}

// ─── Job Description Options ──────────────────────────────────────────────────

interface JobDescOption {
  id: number;
  name: string;
  report: string;
  report_file?: string | null;
  frequency?: string | null;
}

function useJobDescOptions(tenantId: string | number | undefined, orgChartNodeId: number | undefined) {
  return useQuery({
    queryKey: ['kpi-job-desc-options', tenantId, orgChartNodeId],
    queryFn: async (): Promise<JobDescOption[]> => {
      if (!tenantId || !orgChartNodeId) return [];
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/job-description/${tenantId}`;
      const { data } = await axiosInstance.get(url, {
        params: { org_chart_node_id: orgChartNodeId, page_size: 200, order: 'asc', order_by: 'name' },
      });
      const content: JobDescOption[] = data?.data?.content ?? [];
      // Jobdesc berfrekuensi Tahunan/Insidentil tidak diukur lewat KPI. Sebelumnya
      // filternya adalah "punya file template laporan ter-upload", yang ikut
      // menyingkirkan jobdesc yang laporannya sudah dinamai tapi filenya belum
      // diunggah — itulah sebab satu jabatan dengan 3 laporan hanya memunculkan 1.
      return content.filter(jd => isKpiEligibleFrequency(jd.frequency));
    },
    enabled: !!tenantId && !!orgChartNodeId,
    staleTime: 60_000,
  });
}

// ─── Label helpers ─────────────────────────────────────────────────────────────

function getDivisionLabel(key?: string | null): string {
  if (!key) return '—';
  return ORG_DIVISIONS.find(d => d.key === key)?.label ?? key;
}

function getLevelLabel(key?: string | null): string {
  if (!key) return '—';
  return ORG_LEVELS.find(l => l.key === key)?.label ?? key;
}

// ─── KPI Table Row ────────────────────────────────────────────────────────────

interface KpiRowProps {
  index: number;
  orgChartNodeId: number;
  form: ReturnType<typeof useForm<KpiFormValues>>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DynamicTextarea = ({
  registerName,
  register,
  value,
  placeholder,
}: {
  registerName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
  value?: string;
  placeholder?: string;
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative w-full">
      <textarea
        {...register(registerName, {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onBlur: (e: any) => {
            e.target.scrollTop = 0;
            setIsFocused(false);
          },
        })}
        onFocus={() => setIsFocused(true)}
        placeholder={placeholder}
        rows={isFocused ? 3 : 1}
        className={
          'w-full text-xs px-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none transition-all duration-200 block py-1.5' +
          (isFocused ? ' min-h-[36px] overflow-auto' : ' h-8 overflow-hidden')
        }
        style={{ color: isFocused ? 'inherit' : 'transparent' }}
      />
      {!isFocused && (
        <div className="absolute inset-0 pointer-events-none px-2 flex items-center overflow-hidden text-xs">
          <span className="truncate w-full text-gray-900 dark:text-gray-100">
            {value || <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>}
          </span>
        </div>
      )}
    </div>
  );
};

function KpiTableRow({ index, orgChartNodeId, form }: KpiRowProps) {
  const { currentTenant } = useTenantState();
  const { data: jobDescOptions = [], isFetching: isFetchingJobDescs } = useJobDescOptions(
    currentTenant?.id,
    orgChartNodeId,
  );
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = form;

  const selectedJobDescId = useWatch({ control, name: `items.${index}.org_job_description_id` });
  useEffect(() => {
    if (isFetchingJobDescs || !orgChartNodeId || !selectedJobDescId) return;
    const stillAvailable = jobDescOptions.some(jd => jd.id === selectedJobDescId);
    if (!stillAvailable) setValue(`items.${index}.org_job_description_id`, 0);
  }, [isFetchingJobDescs, orgChartNodeId, selectedJobDescId, jobDescOptions, index, setValue]);

  const satuan = useWatch({ control, name: `items.${index}.satuan` });
  const target = useWatch({ control, name: `items.${index}.target` }) ?? 0;
  const realisasi = useWatch({ control, name: `items.${index}.realisasi` }) ?? 0;
  const bobot = useWatch({ control, name: `items.${index}.bobot` }) ?? 0;
  const arah = useWatch({ control, name: `items.${index}.arah` }) ?? 'lebih besar lebih baik';
  const pencapaian = calcPencapaian(satuan, target, realisasi, arah);
  const score = (pencapaian * bobot) / 100;

  const targetKpi = useWatch({ control, name: `items.${index}.target_kpi` });
  const definisiTarget = useWatch({ control, name: `items.${index}.definisi_target` });
  useEffect(() => {
    if (targetKpi === undefined || targetKpi === null) return;
    if (satuan === 'tanggal') {
      setValue(`items.${index}.target`, targetKpi);
      return;
    }
    setValue(`items.${index}.target`, Number(targetKpi) || 0);
  }, [targetKpi, satuan, index, setValue]);

  const handleSatuanChange = (newSatuan: KpiItemValues['satuan']) => {
    setValue(`items.${index}.satuan`, newSatuan);
    if (newSatuan === 'tanggal') {
      setValue(`items.${index}.target_kpi`, '');
      setValue(`items.${index}.target`, '');
      setValue(`items.${index}.realisasi`, '');
    } else {
      setValue(`items.${index}.target_kpi`, getDefaultTargetKpi(newSatuan));
      setValue(`items.${index}.target`, getDefaultTargetValue(newSatuan));
      setValue(`items.${index}.realisasi`, 0);
    }
  };

  const itemErrors = errors.items?.[index];

  return (
    <tr className="border-b border-gray-100 dark:border-gray-700 align-top hover:bg-gray-50/50 dark:hover:bg-gray-800/50">
      <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">{index + 1}</td>

      {/* Report/Tugas */}
      <td className="px-2 py-1">
        <Controller
          control={control}
          name={`items.${index}.org_job_description_id`}
          render={({ field }) => {
            const selectedJobDesc = jobDescOptions.find(jd => jd.id === field.value);
            return (
              <div className="text-xs w-full h-8 px-2 flex items-center bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-lg border border-transparent whitespace-nowrap overflow-hidden text-ellipsis">
                {/* Laporan yang belum dinamai kini ikut muncul, jadi jatuhkan ke
                    nama uraian tugas supaya barisnya tetap bisa dikenali. */}
                {selectedJobDesc ? selectedJobDesc.report?.trim() || selectedJobDesc.name : '—'}
              </div>
            );
          }}
        />
        {itemErrors?.org_job_description_id && (
          <p className="text-xs text-red-500 mt-1">{itemErrors.org_job_description_id.message}</p>
        )}
      </td>

      {/* Bobot */}
      <td className="px-2 py-2">
        {(() => {
          // bobot feeds the live Score and the running total, so it cannot default
          // to '' like other number inputs — NaN would leak into those sums.
          // Clearing a leading 0 on focus fixes the "05" typing bug instead, and
          // blur restores 0 so the calculations always see a number.
          const { onBlur: registerOnBlur, ...bobotField } = register(`items.${index}.bobot`, {
            setValueAs: (value: string) => (value === '' ? 0 : Number(value)),
          });
          return (
            <input
              type="number"
              min={0}
              max={100}
              {...bobotField}
              onFocus={event => {
                if (event.target.value === '0') event.target.select();
              }}
              onBlur={event => {
                if (event.target.value === '') {
                  setValue(`items.${index}.bobot`, 0, { shouldValidate: true });
                }
                return registerOnBlur(event);
              }}
              className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"
              placeholder="0"
            />
          );
        })()}
        {itemErrors?.bobot && <p className="text-xs text-red-500 mt-1">{itemErrors.bobot.message}</p>}
      </td>

      {/* Satuan */}
      <td className="px-2 py-1 border-l border-gray-100 dark:border-gray-700">
        <Controller
          control={control}
          name={`items.${index}.satuan`}
          render={({ field }) => (
            <select
              value={field.value}
              onChange={e => handleSatuanChange(e.target.value as KpiItemValues['satuan'])}
              className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none"
              aria-label="Pilih Satuan"
            >
              <option value="" disabled hidden>
                Pilih Satuan
              </option>
              {KPI_SATUAN_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        />
        {itemErrors?.satuan && <p className="text-xs text-red-500 mt-1">{itemErrors.satuan.message}</p>}
      </td>

      {/* Angka Target */}
      <td className="px-2 py-2">
        {satuan === 'tanggal' ? (
          <input
            type="date"
            aria-label="Angka target"
            {...register(`items.${index}.target_kpi`)}
            className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        ) : (
          <Controller
            control={control}
            name={`items.${index}.target_kpi`}
            render={({ field }) => (
              <div className="relative">
                {satuan === 'rupiah' && (
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">
                    Rp
                  </span>
                )}
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Angka target"
                  value={formatThousandSeparator(field.value == null ? '' : String(field.value))}
                  onChange={e => field.onChange(stripThousandSeparator(e.target.value))}
                  className={`text-xs w-full h-8 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-center ${getPaddingClass(
                    satuan,
                  )}`}
                  placeholder="0"
                />
                {satuan === 'persen' && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">
                    %
                  </span>
                )}
              </div>
            )}
          />
        )}
        {itemErrors?.target_kpi && <p className="text-xs text-red-500 mt-1">{itemErrors.target_kpi.message}</p>}
      </td>

      {/* Definisi Target */}
      <td className="px-2 py-1 border-r border-gray-100 dark:border-gray-700">
        <DynamicTextarea
          registerName={`items.${index}.definisi_target`}
          register={register}
          value={definisiTarget}
          placeholder="Definisi target..."
        />
        {itemErrors?.definisi_target && <p className="text-xs text-red-500">{itemErrors.definisi_target.message}</p>}
      </td>

      {/* Arah */}
      <td className="px-2 py-1 border-r border-gray-100 dark:border-gray-700">
        <Controller
          control={control}
          name={`items.${index}.arah`}
          render={({ field }) => (
            <select
              value={field.value ?? 'lebih besar lebih baik'}
              onChange={e => field.onChange(e.target.value as KpiItemValues['arah'])}
              className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none"
              aria-label="Pilih Arah"
            >
              {KPI_ARAH_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        />
      </td>

      {/* Target */}
      <td className="px-2 py-2 border-l border-gray-100 dark:border-gray-700">
        <Controller
          control={control}
          name={`items.${index}.target`}
          render={({ field }) =>
            satuan === 'tanggal' ? (
              <input
                type="date"
                value={field.value && typeof field.value === 'string' ? field.value : ''}
                onChange={e => field.onChange(e.target.value)}
                className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                aria-label="Target tanggal"
              />
            ) : (
              <div className="relative">
                {satuan === 'rupiah' && (
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">
                    Rp
                  </span>
                )}
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatThousandSeparator(field.value == null ? '' : String(field.value))}
                  onChange={e => {
                    const stripped = stripThousandSeparator(e.target.value);
                    field.onChange(stripped);
                  }}
                  className={`text-xs w-full h-8 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-center ${
                    satuan === 'rupiah' ? 'pl-7 pr-2' : satuan === 'persen' ? 'pr-6 pl-2' : 'px-2'
                  }`}
                  placeholder="0"
                />
                {satuan === 'persen' && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">
                    %
                  </span>
                )}
              </div>
            )
          }
        />
      </td>

      {/* Realisasi */}
      <td className="px-2 py-2">
        <Controller
          control={control}
          name={`items.${index}.realisasi`}
          render={({ field }) =>
            satuan === 'tanggal' ? (
              <input
                type="date"
                value={field.value && typeof field.value === 'string' ? field.value : ''}
                onChange={e => field.onChange(e.target.value)}
                className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                aria-label="Realisasi tanggal"
              />
            ) : (
              <div className="relative">
                {satuan === 'rupiah' && (
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">
                    Rp
                  </span>
                )}
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatThousandSeparator(field.value == null ? '' : String(field.value))}
                  onChange={e => {
                    const stripped = stripThousandSeparator(e.target.value);
                    field.onChange(stripped);
                  }}
                  className={`text-xs w-full h-8 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-center ${
                    satuan === 'rupiah' ? 'pl-7 pr-2' : satuan === 'persen' ? 'pr-6 pl-2' : 'px-2'
                  }`}
                  placeholder="0"
                />
                {satuan === 'persen' && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">
                    %
                  </span>
                )}
              </div>
            )
          }
        />
      </td>

      {/* Pencapaian */}
      <td className="px-3 py-3 text-center text-sm text-gray-600 dark:text-gray-400">{formatPuebi(pencapaian, 1)}%</td>

      {/* Score */}
      <td className="px-3 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-gray-700">
        {formatPuebi(score, 1)}
      </td>

      {/* Delete */}
      <td className="px-1 py-2">
        <div className="flex items-center justify-center">
          {/* Hapus baris dinonaktifkan karena mapping 1:1 dari report */}
        </div>
      </td>
    </tr>
  );
}

// ─── Grouped Row Type ─────────────────────────────────────────────────────────

interface GroupedRow {
  org_chart_node_id: number;
  org_chart_node_name: string;
  division_key?: string | null;
  level_key?: string | null;
}

interface NodeStat {
  count: number;
  totalBobot: number;
  totalScore: number;
}

// ─── KPI List Table ─────────────────────────────────────────────────────────────

interface KpiListTableProps {
  isLoading: boolean;
  rows: GroupedRow[];
  nodeStats: Record<number, NodeStat>;
  isReadOnly: boolean;
  onManage: (row: GroupedRow) => void;
}

function KpiListTableBody({ isLoading, rows, nodeStats, isReadOnly, onManage }: KpiListTableProps) {
  if (isLoading) {
    return (
      <tr>
        <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
          Memuat data...
        </td>
      </tr>
    );
  }

  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
          Belum ada jabatan.
        </td>
      </tr>
    );
  }

  return (
    <>
      {rows.map(row => {
        const stats = nodeStats[row.org_chart_node_id] ?? { count: 0, totalBobot: 0, totalScore: 0 };
        return (
          <tr
            key={row.org_chart_node_id}
            className="group border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/40 dark:hover:bg-gray-700/40"
          >
            <td className="px-4 py-3 font-semibold text-gray-800 dark:text-white sticky left-0 bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800 z-10">
              {row.org_chart_node_name}
            </td>
            <td className="px-4 py-3 text-sm font-semibold uppercase tracking-wide">
              <span className="px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-100">
                {getDivisionLabel(row.division_key)}
              </span>
            </td>
            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{getLevelLabel(row.level_key)}</td>
            <td className="px-4 py-3 text-center">
              <span
                className={`inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium ${
                  stats.count > 0
                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}
              >
                {stats.count} KPI
              </span>
            </td>
            <td className="px-4 py-3 text-center font-semibold text-gray-800 dark:text-gray-100">
              {formatPuebi(stats.totalBobot, 1)}%
            </td>
            <td className="px-4 py-3 text-center font-bold text-blue-600 dark:text-blue-400">
              {formatPuebi(stats.totalScore, 0)}
            </td>
            <td className="px-4 py-3 text-center">
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={() => onManage(row)}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Kelola KPI
                </button>
              )}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function KpiListTable(props: KpiListTableProps) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
      <div className="px-5 py-4 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Daftar Jabatan. Klik <strong>"Kelola KPI"</strong> pada jabatan terkait untuk merumuskan Key Performance
          Indicators (KPI) masing-masing.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-[640px] w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-200 text-left sticky left-0 bg-gray-50 dark:bg-gray-700 z-10">
                NAMA JABATAN
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-200 text-left">DEPARTEMEN</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-200 text-left">LEVEL</th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-200 text-center">
                JUMLAH KPI
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-200 text-center">
                TOTAL BOBOT (%)
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-200 text-center">
                TOTAL SCORE
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-200 text-center">AKSI</th>
            </tr>
          </thead>
          <tbody>
            <KpiListTableBody {...props} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function KPI() {
  const navigate = useNavigate();
  const { tenant_id } = useParams<{ tenant_id: string }>();
  const { currentTenant } = useTenantState();
  const isReadOnly = useIsReadOnly();
  const { kpis, isLoading, fetchKpis, createKpi, updateKpi, deleteKpi } = useKpiState();
  const { orgCharts: orgChartNodes, fetchOrgCharts } = useOrgChartState();
  const refetchCompletionStatus = useRefetchCompletionStatus();
  const [showPanel, setShowPanel] = useState(true);
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [editingNode, setEditingNode] = useState<GroupedRow | null>(null);

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const kpiStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'kpi');
  const videoLink = kpiStep?.video_link;
  const tips = kpiStep?.tips;

  const [searchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [selectedRole] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deletedKpiIds, setDeletedKpiIds] = useState<number[]>([]);

  useEffect(() => {
    if (currentTenant?.id) fetchOrgCharts(currentTenant.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id]);

  const form = useForm<KpiFormValues>({
    resolver: zodResolver(kpiFormSchema),
    defaultValues: { org_chart_node_id: 0, items: [defaultKpiItem()] },
  });

  const { fields, append } = useFieldArray({ control: form.control, name: 'items' });
  const watchedOrgChartNodeId = useWatch({ control: form.control, name: 'org_chart_node_id' });
  const watchedItems = useWatch({ control: form.control, name: 'items' });

  const totalScore = useMemo(() => {
    if (!watchedItems) return 0;
    return watchedItems.reduce((sum, item) => {
      const pencapaian = calcPencapaian(
        item.satuan,
        item.target ?? 0,
        item.realisasi ?? 0,
        item.arah ?? 'lebih besar lebih baik',
      );
      return sum + (pencapaian * (item.bobot ?? 0)) / 100;
    }, 0);
  }, [watchedItems]);

  const totalBobot = useMemo(() => sumKpiBobot(watchedItems), [watchedItems]);
  const isBobotOverflow = totalBobot > KPI_MAX_TOTAL_BOBOT;

  const loadKpis = useCallback(async () => {
    if (!currentTenant?.id) return;
    await fetchKpis(currentTenant.id, { page_size: 1000 });
  }, [currentTenant?.id, fetchKpis]);

  useEffect(() => {
    loadKpis();
  }, [loadKpis]);

  // Auto-populate KPI rows from job descriptions when jabatan is selected
  const { data: autoPopJobDescs = [] } = useJobDescOptions(
    view === 'detail' ? currentTenant?.id : undefined,
    view === 'detail' && watchedOrgChartNodeId ? watchedOrgChartNodeId : undefined,
  );

  useEffect(() => {
    if (view !== 'detail' || !watchedOrgChartNodeId || !autoPopJobDescs.length) return;
    const toItem = (jd: JobDescOption): KpiItemValues => ({ ...defaultKpiItem(), org_job_description_id: jd.id });
    const nodeKpis = kpis.filter(k => k.org_chart_node_id === watchedOrgChartNodeId);
    if (nodeKpis.length === 0) {
      form.setValue('items', autoPopJobDescs.map(toItem));
      return;
    }
    // Jabatan dengan KPI tersimpan: jobdesc yang belum punya baris ditambahkan di bawahnya.
    const present = new Set(form.getValues('items').map(item => item.org_job_description_id));
    const missing = autoPopJobDescs.filter(jd => !present.has(jd.id));
    if (missing.length) append(missing.map(toItem), { shouldFocus: false });
  }, [autoPopJobDescs, view, watchedOrgChartNodeId, form, kpis, append]);

  // Build grouped list rows
  const groupedRows: GroupedRow[] = useMemo(() => {
    return orgChartNodes.map(node => ({
      org_chart_node_id: node.id,
      org_chart_node_name: node.name,
      division_key: node.division_key,
      level_key: node.level_key,
    }));
  }, [orgChartNodes]);

  const filteredRows = useMemo(() => {
    return groupedRows.filter(row => {
      const matchSearch =
        !debouncedSearch || row.org_chart_node_name.toLowerCase().includes(debouncedSearch.toLowerCase());
      const matchRole = !selectedRole || String(row.org_chart_node_id) === selectedRole;
      return matchSearch && matchRole;
    });
  }, [groupedRows, debouncedSearch, selectedRole]);

  // Per-node KPI stats for list table
  const nodeStats = useMemo(() => {
    const map: Record<number, { count: number; totalBobot: number; totalScore: number }> = {};
    for (const kpi of kpis) {
      if (!kpi.org_chart_node_id) continue;
      if (!map[kpi.org_chart_node_id]) map[kpi.org_chart_node_id] = { count: 0, totalBobot: 0, totalScore: 0 };
      const b = kpi.bobot ?? 0;
      const pencapaian = calcPencapaian(
        kpi.satuan,
        kpi.satuan === 'tanggal' ? kpi.target_tanggal ?? '' : kpi.target,
        kpi.satuan === 'tanggal' ? kpi.realisasi_tanggal ?? '' : kpi.realisasi,
        (kpi.arah as KpiItemValues['arah']) ?? 'lebih besar lebih baik',
      );
      map[kpi.org_chart_node_id].count += 1;
      map[kpi.org_chart_node_id].totalBobot += b;
      map[kpi.org_chart_node_id].totalScore += (pencapaian * b) / 100;
    }
    return map;
  }, [kpis]);

  const handleOpenDetail = async (row: GroupedRow) => {
    setDeletedKpiIds([]);
    setEditingNode(row);
    setView('detail');

    await loadKpis();

    const nodeKpis = kpis.filter(k => k.org_chart_node_id === row.org_chart_node_id);
    if (nodeKpis.length) {
      const items: KpiItemValues[] = nodeKpis.map(k => ({
        id: k.id,
        org_job_description_id: k.org_job_description_id,
        satuan: k.satuan,
        target_kpi: k.target_kpi,
        definisi_target: k.definisi_target,
        target: k.satuan === 'tanggal' ? k.target_tanggal ?? '' : k.target,
        realisasi: k.satuan === 'tanggal' ? k.realisasi_tanggal ?? '' : k.realisasi,
        bobot: k.bobot ?? 0,
        arah: (k.arah as KpiItemValues['arah']) ?? 'lebih besar lebih baik',
      }));
      form.reset({ org_chart_node_id: row.org_chart_node_id, items });
    } else {
      form.reset({ org_chart_node_id: row.org_chart_node_id, items: [defaultKpiItem()] });
    }
  };

  const handleBackToList = () => {
    form.reset({ org_chart_node_id: 0, items: [defaultKpiItem()] });
    setDeletedKpiIds([]);
    setEditingNode(null);
    setView('list');
  };

  const handleSaveDetail = form.handleSubmit(async (data: KpiFormValues) => {
    if (!currentTenant?.id) return;
    setIsSaving(true);
    try {
      for (const id of deletedKpiIds) {
        await deleteKpi(currentTenant.id, id);
      }
      for (const item of data.items) {
        const isTanggal = item.satuan === 'tanggal';
        const payload = {
          org_chart_node_id: data.org_chart_node_id,
          kpi: {
            org_job_description_id: item.org_job_description_id,
            satuan: item.satuan,
            target_kpi: String(item.target_kpi),
            definisi_target: item.definisi_target,
            target: isTanggal ? (item.target as string) : Number(item.target) || 0,
            realisasi: isTanggal ? (item.realisasi as string) : Number(item.realisasi) || 0,
            bobot: item.bobot,
            arah: item.arah ?? 'lebih besar lebih baik',
          },
        };
        if (item.id) {
          await updateKpi(currentTenant.id, item.id, payload);
        } else {
          await createKpi(currentTenant.id, payload);
        }
      }
      toast.success('KPI berhasil disimpan');
      await loadKpis();
      await refetchCompletionStatus();
      handleBackToList();
    } catch {
      toast.error('Gagal menyimpan KPI');
    } finally {
      setIsSaving(false);
    }
  });

  const handleSaveAndNext = async () => {
    await refetchCompletionStatus();
    navigate(`/${tenant_id}/reward-punishment`);
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <section className="w-full">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg">KPI Setup (Indikator Kinerja Utama)</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            {view === 'list'
              ? 'Daftar Jabatan. Klik "Kelola KPI" pada jabatan terkait untuk merumuskan Key Performance Indicators (KPI) masing-masing.'
              : 'Kelola KPI untuk jabatan yang dipilih.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          {view === 'list' ? (
            <button
              type="button"
              onClick={handleSaveAndNext}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium"
              style={{ background: '#FD6118' }}
            >
              Simpan & Lanjut 🚀
            </button>
          ) : (
            !isReadOnly && (
              <button
                type="button"
                onClick={handleSaveDetail}
                disabled={isSaving || isBobotOverflow}
                className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
                style={{ background: '#FD6118' }}
              >
                <Check size={15} />
                {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut'}
              </button>
            )
          )}
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          {/* ── LIST VIEW ── */}
          {view === 'list' && (
            <KpiListTable
              isLoading={isLoading}
              rows={filteredRows}
              nodeStats={nodeStats}
              isReadOnly={isReadOnly}
              onManage={handleOpenDetail}
            />
          )}

          {/* ── DETAIL VIEW ── */}
          {view === 'detail' && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
              {/* Detail header */}
              <div className="px-5 py-4 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-4">
                <button
                  type="button"
                  onClick={handleBackToList}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-primary px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <ArrowLeft size={14} /> Kembali ke Daftar Jabatan
                </button>
                {editingNode && (
                  <div className="text-right">
                    <p className="font-bold text-blue-700 dark:text-blue-400">{editingNode.org_chart_node_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {getLevelLabel(editingNode.level_key)}
                      {editingNode.division_key ? ` | Departemen: ${getDivisionLabel(editingNode.division_key)}` : ''}
                    </p>
                  </div>
                )}
              </div>

              {/* KPI table */}
              <div className="overflow-x-auto">
                <table className="min-w-[1325px] w-full table-fixed text-sm border-collapse border border-gray-200 dark:border-gray-700">
                  <colgroup>
                    <col className="w-[45px]" />
                    <col className="w-[190px]" />
                    <col className="w-[75px]" />
                    <col className="w-[105px]" />
                    <col className="w-[110px]" />
                    <col className="w-[240px]" />
                    <col className="w-[140px]" />
                    <col className="w-[110px]" />
                    <col className="w-[110px]" />
                    <col className="w-[75px]" />
                    <col className="w-[75px]" />
                    <col className="w-[50px]" />
                  </colgroup>
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center">
                        NO
                      </th>
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center">
                        NAMA LAPORAN <span className="text-red-400">*</span>
                      </th>
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center">
                        BOBOT % <span className="text-red-400">*</span>
                      </th>
                      <th
                        colSpan={4}
                        className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center border-x border-gray-200 dark:border-gray-700"
                      >
                        Target Laporan
                      </th>
                      <th
                        colSpan={4}
                        className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center border-r border-gray-200 dark:border-gray-700"
                      >
                        Simulasi KPI
                      </th>
                      <th className="px-3 py-3 w-14" />
                    </tr>
                    <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
                      <th />
                      <th />
                      <th />
                      <th className="px-3 py-2 font-medium border-l border-gray-200 dark:border-gray-700 text-center">
                        SATUAN <span className="text-red-400">*</span>
                      </th>
                      <th className="px-3 py-2 font-medium text-center">
                        ANGKA TARGET <span className="text-red-400">*</span>
                      </th>
                      <th className="px-3 py-2 font-medium text-center">
                        DEFINISI TARGET KPI <span className="text-red-400">*</span>
                      </th>
                      <th className="px-3 py-2 font-medium border-r border-gray-200 dark:border-gray-700 text-center">
                        ARAH TARGET <span className="text-red-400">*</span>
                      </th>
                      <th className="px-3 py-2 font-medium border-l border-gray-200 dark:border-gray-700 text-center">
                        TARGET
                      </th>
                      <th className="px-3 py-2 font-medium text-center">REALISASI</th>
                      <th className="px-3 py-2 font-medium text-center">PENCAPAIAN</th>
                      <th className="px-3 py-2 font-medium border-r border-gray-200 dark:border-gray-700 text-center">
                        SCORE
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((field, index) => (
                      <KpiTableRow key={field.id} index={index} orgChartNodeId={watchedOrgChartNodeId} form={form} />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50/50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 text-sm font-semibold">
                      <td
                        colSpan={2}
                        className="px-3 py-3 text-right text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-gray-700"
                      >
                        Total Bobot:
                      </td>
                      <td
                        className={`px-2 py-3 text-center ${
                          isBobotOverflow ? 'text-red-600' : 'text-blue-600 dark:text-blue-400'
                        }`}
                      >
                        {formatPuebi(totalBobot, 1)}%
                      </td>
                      <td
                        colSpan={7}
                        className="px-3 py-3 text-right text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-gray-700"
                      >
                        Total Score:
                      </td>
                      <td className="px-3 py-3 text-center text-blue-600 dark:text-blue-400 border-r border-gray-100 dark:border-gray-700">
                        {formatPuebi(totalScore, 0)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Footer */}
              <div className="px-4 py-3 flex items-center justify-between">
                <div />
              </div>

              {isBobotOverflow && (
                <p className="text-sm text-red-500 px-5 pb-3" data-testid="kpi-bobot-overflow-warning">
                  Total % bobot adalah {formatPuebi(totalBobot, 1)}%, melebihi batas {KPI_MAX_TOTAL_BOBOT}%. Kurangi
                  salah satu nilai bobot.
                </p>
              )}
            </div>
          )}
        </div>

        {showPanel && (
          <div className="w-full xl:w-80 shrink-0 min-w-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} />
          </div>
        )}
      </div>
    </section>
  );
}
